import React, { useState, useEffect } from "react";
import axios from "axios";
import { DateTime } from "luxon";
import Swal from "sweetalert2";

import API_URL from "../config";


const formatHorarioLabel = (h) => {
  const fecha = DateTime.fromISO(String(h.fecha).slice(0, 10)).toFormat("dd/MM/yyyy");
  const inicio = DateTime.fromFormat(h.hora_inicio, "HH:mm:ss").toFormat("hh:mm a");
  const fin = DateTime.fromFormat(h.hora_fin, "HH:mm:ss").toFormat("hh:mm a");
  return `${fecha} · ${inicio} - ${fin}`;
};

const estilosModal = {
  overlay: "fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4",
  caja: "bg-white rounded-2xl shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto",
  input: "w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400",
  label: "block text-sm font-medium text-gray-700 mb-1",
};

/* ===============================
   🔎 FILTRO DE RANGO PARA LISTAS DE HORARIOS
   (para no mostrar de una vez el montón completo de horarios
   disponibles de un profesor — se puede acotar por día/semana/mes)
=============================== */
const FILTROS_RANGO = [
  { key: "dia", label: "Hoy" },
  { key: "semana", label: "Esta semana" },
  { key: "mes", label: "Este mes" },
  { key: "todos", label: "Todos" },
];

const filtrarHorariosPorRango = (horarios, rango) => {
  if (rango === "todos") return horarios;

  const ahora = DateTime.now();
  const limite =
    rango === "dia"
      ? ahora.endOf("day")
      : rango === "semana"
      ? ahora.plus({ days: 7 })
      : ahora.plus({ days: 30 }); // mes

  return horarios.filter((h) => {
    const fecha = DateTime.fromISO(String(h.fecha).slice(0, 10));
    return fecha <= limite;
  });
};

const SelectorRango = ({ valor, onChange }) => (
  <div className="flex gap-2 flex-wrap mb-2">
    {FILTROS_RANGO.map((f) => (
      <button
        key={f.key}
        type="button"
        onClick={() => onChange(f.key)}
        className={`px-3 py-1 rounded-full text-xs font-semibold border transition ${
          valor === f.key
            ? "bg-blue-600 text-white border-blue-600"
            : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
        }`}
      >
        {f.label}
      </button>
    ))}
  </div>
);

/* ===============================
   🆕 MODAL: NUEVA CITA A NOMBRE DE UN PADRE
=============================== */
const NuevaCitaModal = ({ profesores, cargarHorariosDisponibles, onClose, onCreada }) => {
  const [profesorId, setProfesorId] = useState("");
  const [horarios, setHorarios] = useState([]);
  const [horarioId, setHorarioId] = useState("");
  const [rangoFiltro, setRangoFiltro] = useState("dia");
  const [cargandoHorarios, setCargandoHorarios] = useState(false);
  const [alumnoNombre, setAlumnoNombre] = useState("");
  const [grado, setGrado] = useState("");
  const [correoPadre, setCorreoPadre] = useState("");
  const [comentario, setComentario] = useState("");
  const [guardando, setGuardando] = useState(false);

  const horariosVisibles = filtrarHorariosPorRango(horarios, rangoFiltro);

  useEffect(() => {
    if (!profesorId) {
      setHorarios([]);
      setHorarioId("");
      return;
    }
    setCargandoHorarios(true);
    cargarHorariosDisponibles(profesorId).then((h) => {
      setHorarios(h);
      setHorarioId("");
      setCargandoHorarios(false);
    });
  }, [profesorId]); // eslint-disable-line

  const guardar = async () => {
    if (!profesorId || !horarioId || !alumnoNombre.trim() || !correoPadre.trim()) {
      alert("Completa profesor, horario, nombre del alumno y correo del padre.");
      return;
    }
    setGuardando(true);
    try {
      await axios.post(`${API_URL}/api/citas/reservar`, {
        correo_padre: correoPadre.trim(),
        id_profesor: profesorId,
        id_horario: horarioId,
        alumno_nombre: alumnoNombre.trim(),
        grado,
        comentario,
      });
      alert("✅ Cita creada correctamente a nombre del padre");
      onCreada();
    } catch (err) {
      console.error(err);
      alert("❌ No se pudo crear la cita");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className={estilosModal.overlay}>
      <div className={estilosModal.caja}>
        <h3 className="text-lg font-bold text-blue-800 mb-4">
          ➕ Nueva cita a nombre de un padre
        </h3>

        <div className="space-y-3">
          <div>
            <label className={estilosModal.label}>Profesor</label>
            <select className={estilosModal.input} value={profesorId} onChange={(e) => setProfesorId(e.target.value)}>
              <option value="">Selecciona un profesor...</option>
              {profesores.map((p) => (
                <option key={p.id} value={p.id}>{p.nombre}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={estilosModal.label}>Horario disponible</label>

            {profesorId && !cargandoHorarios && horarios.length > 0 && (
              <SelectorRango valor={rangoFiltro} onChange={setRangoFiltro} />
            )}

            <select
              className={estilosModal.input}
              value={horarioId}
              onChange={(e) => setHorarioId(e.target.value)}
              disabled={!profesorId || cargandoHorarios}
            >
              <option value="">
                {cargandoHorarios
                  ? "Cargando..."
                  : horarios.length === 0 && profesorId
                  ? "Sin horarios disponibles"
                  : horariosVisibles.length === 0
                  ? "Nada en este rango — prueba con \"Todos\""
                  : "Selecciona un horario..."}
              </option>
              {horariosVisibles.map((h) => (
                <option key={h.id} value={h.id}>{formatHorarioLabel(h)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={estilosModal.label}>Nombre del alumno</label>
            <input className={estilosModal.input} value={alumnoNombre} onChange={(e) => setAlumnoNombre(e.target.value)} />
          </div>

          <div>
            <label className={estilosModal.label}>Grado</label>
            <input className={estilosModal.input} value={grado} onChange={(e) => setGrado(e.target.value)} />
          </div>

          <div>
            <label className={estilosModal.label}>Correo del padre</label>
            <input type="email" className={estilosModal.input} value={correoPadre} onChange={(e) => setCorreoPadre(e.target.value)} />
          </div>

          <div>
            <label className={estilosModal.label}>Comentario (opcional)</label>
            <textarea className={estilosModal.input} rows={2} value={comentario} onChange={(e) => setComentario(e.target.value)} />
          </div>
        </div>

        <div className="flex gap-3 mt-5">
          <button
            onClick={guardar}
            disabled={guardando}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition"
          >
            {guardando ? "Guardando..." : "Crear cita"}
          </button>
          <button onClick={onClose} className="flex-1 bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded-lg font-semibold transition">
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

/* ===============================
   🔁 MODAL: REAGENDAR CITA
=============================== */
const ReagendarModal = ({ cita, cargarHorariosDisponibles, onClose, onReagendada }) => {
  const [horarios, setHorarios] = useState([]);
  const [horarioId, setHorarioId] = useState("");
  const [rangoFiltro, setRangoFiltro] = useState("dia");
  const [nota, setNota] = useState(cita.nota || "");
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  const horariosVisibles = filtrarHorariosPorRango(horarios, rangoFiltro);

  useEffect(() => {
    cargarHorariosDisponibles(cita.profesor_id).then((h) => {
      setHorarios(h);
      setCargando(false);
    });
  }, [cita.profesor_id]); // eslint-disable-line

  const guardar = async () => {
    if (!horarioId) {
      alert("Elige el nuevo horario.");
      return;
    }
    setGuardando(true);
    try {
      await axios.post(`${API_URL}/api/citas/${cita.id}/reagendar`, {
        nuevo_horario_id: horarioId,
        nota,
      });
      alert("✅ Cita reagendada correctamente");
      onReagendada();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "❌ No se pudo reagendar la cita");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className={estilosModal.overlay}>
      <div className={estilosModal.caja}>
        <h3 className="text-lg font-bold text-blue-800 mb-1">
          🔁 Reagendar cita
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          {cita.alumno_nombre} con {cita.profesor_nombre} — actualmente {cita.fecha_obj?.toFormat("dd/MM/yyyy")}
        </p>

        <label className={estilosModal.label}>Nuevo horario disponible</label>

        {!cargando && horarios.length > 0 && (
          <SelectorRango valor={rangoFiltro} onChange={setRangoFiltro} />
        )}

        <select
          className={estilosModal.input}
          value={horarioId}
          onChange={(e) => setHorarioId(e.target.value)}
          disabled={cargando}
        >
          <option value="">
            {cargando
              ? "Cargando..."
              : horarios.length === 0
              ? "Sin horarios disponibles"
              : horariosVisibles.length === 0
              ? "Nada en este rango — prueba con \"Todos\""
              : "Selecciona un horario..."}
          </option>
          {horariosVisibles.map((h) => (
            <option key={h.id} value={h.id}>{formatHorarioLabel(h)}</option>
          ))}
        </select>

        <label className={`${estilosModal.label} mt-3`}>Motivo del cambio (opcional)</label>
        <textarea
          className={estilosModal.input}
          rows={3}
          placeholder="Ej: el profesor tuvo un imprevisto, el padre pidió otro horario..."
          value={nota}
          onChange={(e) => setNota(e.target.value)}
        />

        <div className="flex gap-3 mt-5">
          <button
            onClick={guardar}
            disabled={guardando}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition"
          >
            {guardando ? "Guardando..." : "Reagendar"}
          </button>
          <button onClick={onClose} className="flex-1 bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded-lg font-semibold transition">
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

const GestionarCitas = () => {

  const [citas, setCitas] = useState([]);
  const [profesores, setProfesores] = useState([]);

  const [filterFechaInicio, setFilterFechaInicio] =
    useState("");

  const [filterFechaFin, setFilterFechaFin] =
    useState("");

  const [filterProfesorNombre, setFilterProfesorNombre] =
    useState("todos");

  const [searchProfesorNombre, setSearchProfesorNombre] =
    useState("");

  const [mostrarHistorial, setMostrarHistorial] =
    useState(false);
  const [paginaActual, setPaginaActual] =
  useState(1);

  const citasPorPagina = 50;

  // 🆕 Nueva cita a nombre de un padre / Reagendar cita existente
  const [mostrarNuevaCita, setMostrarNuevaCita] = useState(false);
  const [citaReagendando, setCitaReagendando] = useState(null);

  /* =========================================
     🔵 FETCH CITAS
  ========================================= */
  const fetchCitas = async (historial = false) => {

    try {

    //  const url = historial
     //   ? "http://localhost:5000/api/citas/historial"
      //  : "http://localhost:5000/api/citas/todas";

const url = historial
  ? `${API_URL}/api/citas/historial`
  : `${API_URL}/api/citas/todas`;



      const res = await axios.get(url);

      setCitas(
        res.data.map((cita) => ({
          ...cita,

          fecha_obj: cita.fecha
            ? DateTime.fromISO(cita.fecha, {
                zone: "utc",
              })
                .setZone("America/Managua")
                .startOf("day")
            : null,
        }))
      );

    } catch (err) {

      console.error(err);
    }
  };

  /* =========================================
     👨‍🏫 FETCH PROFESORES
  ========================================= */
  const fetchProfesores = async () => {

    try {

     // const res = await axios.get(
       // "http://localhost:5000/api/profesores"
    //);

const res = await axios.get(
  `${API_URL}/api/profesores`
);



      setProfesores(res.data);

    } catch (err) {

      console.error(err);
    }
  };

  /* =========================================
     📅 HORARIOS DISPONIBLES DE UN PROFESOR
     (usado tanto para "Nueva Cita" como para "Reagendar")
  ========================================= */
  const cargarHorariosDisponibles = async (profesorId) => {
    if (!profesorId) return [];
    try {
      const res = await axios.get(`${API_URL}/api/horarios/profesor/${profesorId}`);
      const ahora = DateTime.now();

      return (res.data || [])
        .filter((h) => {
          if (h.disponible !== 1) return false;
          const fin = DateTime.fromISO(`${String(h.fecha).slice(0, 10)}T${h.hora_fin}`);
          return fin > ahora;
        })
        .sort((a, b) => {
          const claveA = `${a.fecha}${a.hora_inicio}`;
          const claveB = `${b.fecha}${b.hora_inicio}`;
          return claveA.localeCompare(claveB);
        });
    } catch (err) {
      console.error("Error cargando horarios disponibles:", err);
      return [];
    }
  };

  useEffect(() => {

    fetchCitas(mostrarHistorial);
    fetchProfesores();

  }, [mostrarHistorial]);

  /* =========================================
     ❌ CANCELAR CITA
  ========================================= */
  const cancelarCita = async (id) => {

    const confirm = await Swal.fire({
      title: "¿Cancelar cita?",
      text: "Se notificará al padre.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, cancelar",
    });

    if (!confirm.isConfirmed) return;

    try {

   await axios.post(
  `${API_URL}/api/citas/admin/${id}`
);



      fetchCitas(mostrarHistorial);

    } catch (err) {

      console.error(err);
    }
  };

  /* =========================================
     🟢 CAMBIAR ESTADO
  ========================================= */
  const cambiarEstado = async (id, estado) => {

    try {

     
await axios.post(
  `${API_URL}/api/citas/${id}/estado`,
  { estado }
);


      fetchCitas(mostrarHistorial);

    } catch (err) {

      console.error(err);
    }
  };

  /* =========================================
     🔎 FILTRAR CITAS
  ========================================= */
  const filteredCitas = citas
    .filter((cita) => {


      const matchProfesor =
        filterProfesorNombre === "todos" ||
        cita.profesor_nombre?.toLowerCase() ===
          filterProfesorNombre.toLowerCase();

      const busqueda = searchProfesorNombre.toLowerCase();

      const matchBusqueda =
        !busqueda ||
        cita.profesor_nombre?.toLowerCase().includes(busqueda) ||
        cita.correo_padre?.toLowerCase().includes(busqueda) ||
        cita.alumno_nombre?.toLowerCase().includes(busqueda);

      const inicio = filterFechaInicio
        ? DateTime.fromISO(filterFechaInicio)
        : null;

      const fin = filterFechaFin
        ? DateTime.fromISO(filterFechaFin)
        : null;

      const matchFecha =
        (!inicio ||
          (cita.fecha_obj && cita.fecha_obj >= inicio)) &&
        (!fin ||
          (cita.fecha_obj && cita.fecha_obj <= fin));

      return (
        matchProfesor &&
        matchBusqueda &&
        matchFecha
      );
    })

    .sort((a, b) => {

      const diffFecha =
        a.fecha_obj > b.fecha_obj ? 1 :
        a.fecha_obj < b.fecha_obj ? -1 : 0;

      const diff =
        diffFecha !== 0
          ? diffFecha
          : (a.hora_inicio || "").localeCompare(b.hora_inicio || "");

      // 📚 Historial: más reciente primero. Pendientes: más próxima primero.
      return mostrarHistorial ? -diff : diff;
    });
/* =========================================
   📄 PAGINACIÓN
========================================= */

const totalPaginas = Math.ceil(
  filteredCitas.length / citasPorPagina
);

const indexInicio =
  (paginaActual - 1) * citasPorPagina;

const indexFin =
  indexInicio + citasPorPagina;

const citasPaginadas =
  filteredCitas.slice(
    indexInicio,
    indexFin
  );

/* =========================================
   📅 AGRUPAR POR FECHA (para separar visualmente
   cada día, en vez de una sola tabla plana)
========================================= */
const citasAgrupadas = citasPaginadas.reduce((grupos, cita) => {
  const clave = cita.fecha_obj ? cita.fecha_obj.toFormat("yyyy-MM-dd") : "sin-fecha";
  if (!grupos[clave]) {
    grupos[clave] = {
      etiqueta: cita.fecha_obj
        ? cita.fecha_obj.setLocale("es").toFormat("cccc d 'de' LLLL")
        : "Sin fecha",
      citas: [],
    };
  }
  grupos[clave].citas.push(cita);
  return grupos;
}, {});

const gruposFecha = Object.values(citasAgrupadas);

// Color fijo para la franja de cada fecha — separa visualmente las
// fechas de las filas de citas (blancas), sin variar de una fecha a otra
const colorFranjaFecha = "bg-blue-100 text-blue-800 border-blue-200";


  /* =========================================
     🕒 FORMATEAR HORA
  ========================================= */
  const formatHora = (hora) =>
    DateTime.fromFormat(
      hora,
      "HH:mm:ss"
    ).toFormat("hh:mm a");

  /* =========================================
     🎨 COLORES ESTADOS
  ========================================= */
  const getEstadoColor = (estado) => {

    switch (estado) {

      case "atendida":
        return `
          bg-green-100
          text-green-700
          border
          border-green-300
        `;

      case "no_asistio":
      case "cancelada_admin":
      case "cancelada_profesor":
        return `
          bg-red-100
          text-red-700
          border
          border-red-300
        `;

      case "pendiente":
      default:
        return `
          bg-yellow-100
          text-yellow-700
          border
          border-yellow-300
        `;
    }
  };

  const getEstadoBorderColor = (estado) => {
    switch (estado) {
      case "atendida":
        return "#22c55e";
      case "no_asistio":
      case "cancelada_admin":
      case "cancelada_profesor":
        return "#ef4444";
      case "pendiente":
      default:
        return "#eab308";
    }
  };

  return (

    <div className="p-3 md:p-8 bg-gray-50 min-h-screen">

      {/* =====================================
          TITULO
      ===================================== */}
      <div className="mb-5 md:mb-8">

        <h2 className="text-2xl md:text-4xl font-extrabold text-blue-800">

          Gestionar Citas{" "}

          <span className="text-lg md:text-2xl text-gray-500">

            {mostrarHistorial
              ? "(Historial)"
              : "(Pendientes)"}

          </span>

        </h2>

      </div>

      {/* =====================================
          FILTROS
      ===================================== */}
      <div className="
        bg-white
        rounded-2xl
        shadow-md
        p-4
        md:p-5
        mb-5
        md:mb-8
      ">

        <div className="flex flex-col md:flex-row md:flex-wrap gap-3 md:gap-4">

          <input
            type="text"
            placeholder="Buscar por profesor, padre o alumno..."
            value={searchProfesorNombre}
            onChange={(e) =>
              setSearchProfesorNombre(e.target.value)
            }
            className="
              w-full
              md:w-auto
              border
              border-gray-300
              px-4
              py-2
              rounded-lg
              focus:outline-none
              focus:ring-2
              focus:ring-blue-400
            "
          />

          <div className="flex gap-3 md:contents">
            <input
              type="date"
              value={filterFechaInicio}
              onChange={(e) =>
                setFilterFechaInicio(e.target.value)
              }
              className="
                w-full
                md:w-auto
                border
                border-gray-300
                px-4
                py-2
                rounded-lg
                focus:outline-none
                focus:ring-2
                focus:ring-blue-400
              "
            />

            <input
              type="date"
              value={filterFechaFin}
              onChange={(e) =>
                setFilterFechaFin(e.target.value)
              }
              className="
                w-full
                md:w-auto
                border
                border-gray-300
                px-4
                py-2
                rounded-lg
                focus:outline-none
                focus:ring-2
                focus:ring-blue-400
              "
            />
          </div>

          <select
            value={filterProfesorNombre}
            onChange={(e) =>
              setFilterProfesorNombre(e.target.value)
            }
            className="
              w-full
              md:w-auto
              border
              border-gray-300
              px-4
              py-2
              rounded-lg
              bg-white
              focus:outline-none
              focus:ring-2
              focus:ring-blue-400
            "
          >

            <option value="todos">
              Todos
            </option>

            {profesores.map((p) => (

              <option
                key={p.id}
                value={p.nombre}
              >
                {p.nombre}
              </option>

            ))}

          </select>

          <button


          onClick={() => {
  setMostrarHistorial(!mostrarHistorial);
  setPaginaActual(1);
}}


            className="
              w-full
              md:w-auto
              bg-blue-600
              hover:bg-blue-700
              text-white
              px-6
              py-2
              rounded-lg
              font-semibold
              transition
              duration-200
              shadow
            "
          >

            {mostrarHistorial
              ? "Ver pendientes"
              : "Ver historial"}

          </button>

          <button
            onClick={() => setMostrarNuevaCita(true)}
            className="
              w-full
              md:w-auto
              bg-emerald-600
              hover:bg-emerald-700
              text-white
              px-6
              py-2
              rounded-lg
              font-semibold
              transition
              duration-200
              shadow
            "
          >
            ➕ Nueva Cita
          </button>

        </div>

      </div>

      {/* =====================================
          TABLA
      ===================================== */}
      <div className="
        bg-white
        rounded-2xl
        shadow-lg
        border
        border-gray-100
        overflow-hidden
      ">

      {/* Tabla — solo escritorio */}
      <div className="hidden md:block overflow-x-auto max-h-[70vh] overflow-y-auto">
<table className="min-w-full text-sm md:text-base whitespace-nowrap">

          {/* ================================= */}
          <thead className="
            bg-gradient-to-r
            from-blue-700
            to-blue-500
            text-white
            sticky
            top-0
            z-10
          ">

            <tr>

              <th className="px-4 py-4 text-center">
                Hora
              </th>

              <th className="px-4 py-4 text-center">
                Alumno
              </th>



              <th className="px-4 py-4 text-center">
               Padre
               </th>




              <th className="px-4 py-4 text-center">
                Profesor
              </th>

              <th className="px-4 py-4 text-center">
                Nota
              </th>

              <th className="px-4 py-4 text-center">
                Estado
              </th>

              {!mostrarHistorial && (

                <th className="px-4 py-4 text-center">
                  Acción
                </th>

              )}

            </tr>

          </thead>

          {/* ================================= */}
          <tbody>

            {filteredCitas.length === 0 ? (

              <tr>

                <td
                  colSpan={mostrarHistorial ? 6 : 7}
                  className="
                    text-center
                    py-10
                    text-gray-500
                  "
                >
                  No hay citas
                </td>

              </tr>

            ) : (
 gruposFecha.map((grupo) => (
   <React.Fragment key={grupo.etiqueta}>

     {/* ENCABEZADO DE FECHA */}
     <tr>
       <td
         colSpan={mostrarHistorial ? 6 : 7}
         className={`px-4 py-2 font-bold text-sm capitalize border-b ${colorFranjaFecha}`}
       >
         📅 {grupo.etiqueta}{" "}
         <span className="ml-2 text-xs font-semibold bg-blue-600 text-white px-2 py-0.5 rounded-full align-middle">
           {grupo.citas.length}
         </span>
       </td>
     </tr>

     {grupo.citas.map((cita) => (

                <tr
                  key={cita.id}
                  className="
                    border-b
                    even:bg-gray-50/60
                    hover:bg-blue-50
                    transition
                    duration-200
                  "
                >

                  {/* HORA */}
                  <td
                    className="px-4 py-4 font-semibold"
                    style={{ borderLeft: `4px solid ${getEstadoBorderColor(cita.estado)}` }}
                  >

                    {formatHora(cita.hora_inicio)}
                    {" – "}
                    {formatHora(cita.hora_fin)}

                  </td>

                  {/* ALUMNO */}
                  <td className="px-4 py-4 font-medium">
                    {cita.alumno_nombre}

                  </td>
                  {/* PADRE */}
                  <td className="px-4 py-4">
                   {cita.correo_padre}
                   </td>
                    

                  {/* PROFESOR */}
                  <td className="px-4 py-4">
                    {cita.profesor_nombre}
                  </td>
 
                 {/* NOTA */}
                <td className="px-4 py-4">
                {cita.nota || "-"}
                 </td>


                  {/* ESTADO */}
                  <td className="px-4 py-4">
<span
  className={`
    px-4
    py-1
    rounded-full
    text-sm
    font-bold
    capitalize
    whitespace-nowrap
    inline-block
    ${getEstadoColor(cita.estado)}
  `}
>


                   


                      {cita.estado.replace("_", " ")}
                    </span>

                  </td>

                  {/* ACCIONES */}
                  {!mostrarHistorial && (

                    <td className="px-4 py-4">

                      <select
                        value=""
                        onChange={(e) => {
                          const accion = e.target.value;
                          if (!accion) return;
                          if (accion === "reagendar") setCitaReagendando(cita);
                          else if (accion === "cancelar") cancelarCita(cita.id);
                          else cambiarEstado(cita.id, accion);
                        }}
                        className="
                          border
                          border-gray-300
                          px-3
                          py-2
                          rounded-lg
                          text-sm
                          bg-white
                          focus:outline-none
                          focus:ring-2
                          focus:ring-blue-400
                        "
                      >
                        <option value="">Acción</option>
                        <option value="atendida">Marcar atendida</option>
                        <option value="no_asistio">Marcar no asistió</option>
                        <option value="reagendar">Reagendar</option>
                        <option value="cancelar">Cancelar</option>
                      </select>

                    </td>

                  )}

                </tr>

     ))}
   </React.Fragment>
 ))
            )}

          </tbody>

        </table>
      </div>

      {/* Tarjetas — solo móvil */}
      <div className="md:hidden flex flex-col gap-3 p-3">

        {filteredCitas.length === 0 ? (
          <div className="text-center py-10 text-gray-500">
            No hay citas
          </div>
        ) : (
          gruposFecha.map((grupo) => (
            <div key={grupo.etiqueta}>

              <div className={`text-sm font-bold mb-2 capitalize flex items-center gap-2 px-3 py-2 rounded-lg border ${colorFranjaFecha}`}>
                📅 {grupo.etiqueta}
                <span className="text-xs font-semibold bg-blue-600 text-white px-2 py-0.5 rounded-full">
                  {grupo.citas.length}
                </span>
              </div>

              <div className="flex flex-col gap-3 mb-1">
                {grupo.citas.map((cita) => (
                  <div
                    key={cita.id}
                    className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-col gap-2"
                    style={{ borderLeft: `4px solid ${getEstadoBorderColor(cita.estado)}` }}
                  >

                    <div className="flex justify-between items-start gap-2">
                      <p className="font-bold text-gray-800">
                        {formatHora(cita.hora_inicio)} – {formatHora(cita.hora_fin)}
                      </p>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-bold capitalize whitespace-nowrap ${getEstadoColor(cita.estado)}`}
                      >
                        {cita.estado.replace("_", " ")}
                      </span>
                    </div>

                    <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 text-sm mt-1">
                      <span className="text-gray-400">Alumno:</span>
                      <span className="font-medium text-gray-800">{cita.alumno_nombre}</span>

                      <span className="text-gray-400">Padre:</span>
                      <span className="text-gray-800 break-all">{cita.correo_padre}</span>

                      <span className="text-gray-400">Profesor:</span>
                      <span className="text-gray-800">{cita.profesor_nombre}</span>

                      {cita.nota && (
                        <>
                          <span className="text-gray-400">Nota:</span>
                          <span className="text-gray-800">{cita.nota}</span>
                        </>
                      )}
                    </div>

                    {!mostrarHistorial && (
                      <div className="mt-2">
                        <select
                          value=""
                          onChange={(e) => {
                            const accion = e.target.value;
                            if (!accion) return;
                            if (accion === "reagendar") setCitaReagendando(cita);
                            else if (accion === "cancelar") cancelarCita(cita.id);
                            else cambiarEstado(cita.id, accion);
                          }}
                          className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                        >
                          <option value="">Acción</option>
                          <option value="atendida">Marcar atendida</option>
                          <option value="no_asistio">Marcar no asistió</option>
                          <option value="reagendar">Reagendar</option>
                          <option value="cancelar">Cancelar</option>
                        </select>
                      </div>
                    )}

                  </div>
                ))}
              </div>
            </div>
          ))
        )}

      </div>

<div className="flex justify-center items-center gap-2 py-6">

  <button
    disabled={paginaActual === 1}
    onClick={() => setPaginaActual(p => p - 1)}
    className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
  >
    Anterior
  </button>

  <span>
    Página {paginaActual} de {totalPaginas || 1}
  </span>

  <button
    disabled={paginaActual === totalPaginas}
    onClick={() => setPaginaActual(p => p + 1)}
    className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
  >
    Siguiente
  </button>

</div>


      </div>

      {mostrarNuevaCita && (
        <NuevaCitaModal
          profesores={profesores}
          cargarHorariosDisponibles={cargarHorariosDisponibles}
          onClose={() => setMostrarNuevaCita(false)}
          onCreada={() => {
            setMostrarNuevaCita(false);
            fetchCitas(mostrarHistorial);
          }}
        />
      )}

      {citaReagendando && (
        <ReagendarModal
          cita={citaReagendando}
          cargarHorariosDisponibles={cargarHorariosDisponibles}
          onClose={() => setCitaReagendando(null)}
          onReagendada={() => {
            setCitaReagendando(null);
            fetchCitas(mostrarHistorial);
          }}
        />
      )}

    </div>
  );
};

export default GestionarCitas;